import { useT } from '../hooks/useT';
import type { SupportedLocale } from '../i18n/resolveLocale';

const LABEL: Record<SupportedLocale, string> = { de: 'Deutsch', en: 'English' };
const SHORT_LABEL: Record<SupportedLocale, string> = { de: 'DE', en: 'EN' };

type Props = {
  /** Small segmented `DE | EN` control with no label text, for tight top-bar placements. */
  compact?: boolean;
};

export function LanguageToggle({ compact = false }: Props = {}) {
  const { locale, setLocale, t } = useT();

  if (compact) {
    return (
      <div
        role="group"
        aria-label={t('offer.language_toggle_label')}
        className="inline-flex items-center gap-0.5 rounded-full border border-slate-300 bg-white p-0.5 text-xs"
      >
        {(['de', 'en'] as const).map((l) => {
          const active = locale === l;
          return (
            <button
              key={l}
              type="button"
              onClick={() => setLocale(l)}
              aria-pressed={active}
              aria-label={LABEL[l]}
              title={LABEL[l]}
              className={[
                'rounded-full px-2 py-0.5 font-semibold leading-5 transition',
                active ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-100',
              ].join(' ')}
            >
              {SHORT_LABEL[l]}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-2 text-sm">
      <span className="text-slate-600">{t('offer.language_toggle_label')}:</span>
      {(['de', 'en'] as const).map((l) => {
        const active = locale === l;
        return (
          <button
            key={l}
            type="button"
            onClick={() => setLocale(l)}
            aria-pressed={active}
            className={[
              'rounded px-2 py-1 text-sm font-medium transition',
              active
                ? 'bg-brand text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200',
            ].join(' ')}
          >
            {LABEL[l]}
          </button>
        );
      })}
    </div>
  );
}
